import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
  type VisibilityState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  UpSmallLine,
  DownSmallLine,
  More1Line,
  CheckLine,
  CloseLine,
  TableLine,
  LayoutLine,
} from '@mingcute/react';

import { cn } from '../lib/cn';
import { useMediaQuery } from '../hooks/use-media-query';

import { Skeleton } from './skeleton';
import { EmptyState } from './empty-state';

/* ----------------------------------------------------------------------- */
/*                              Types                                      */
/* ----------------------------------------------------------------------- */

export type SortDir = 'asc' | 'desc';

export interface SortState {
  id: string;
  dir: SortDir;
}

export interface DataTableColumn<T> {
  /** Unique stable id (used for sort + persisted column visibility). */
  id: string;
  /** Header label. */
  header: React.ReactNode;
  /** Cell renderer. Receives the row. */
  cell: (row: T, index: number) => React.ReactNode;
  /** Enable sorting on this column (server-side: emits onSortChange). */
  sortable?: boolean;
  /** Initial width (px). Default 160. */
  width?: number;
  /** Min width (px). Default 64. */
  minWidth?: number;
  /** When `true` this is the trailing actions column (rendered on row hover). */
  actions?: (row: T) => React.ReactNode;
  /** Show in mobile card view. Defaults: first 2 non-action columns. */
  primary?: boolean;
  /** Hide column from visibility toggles (always visible). */
  pinned?: boolean;
  /** Header alignment. */
  align?: 'left' | 'right' | 'center';
}

export type DataTableDensity = 'compact' | 'normal' | 'comfortable';

export interface DataTableFilter {
  id: string;
  label: React.ReactNode;
  value?: React.ReactNode;
  onRemove?: () => void;
}

export interface DataTableProps<T> {
  /** Stable id used to persist density + column visibility to localStorage. */
  id?: string;
  columns: DataTableColumn<T>[];
  data: T[];
  /** Stable row id resolver. Defaults to index — caller SHOULD provide. */
  getRowId?: (row: T, index: number) => string;
  loading?: boolean;
  /** Server-side total — defaults to data.length. */
  total?: number;
  pageIndex?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** Multi-select selection. Set of row ids. */
  selection?: Set<string>;
  onSelectionChange?: (selection: Set<string>) => void;
  /** Render bulk actions in the sticky footer when ≥1 row selected. */
  bulkActions?: (selected: Set<string>) => React.ReactNode;
  /** Filter chips above the table. */
  filters?: DataTableFilter[];
  density?: DataTableDensity;
  onDensityChange?: (density: DataTableDensity) => void;
  onRowClick?: (row: T) => void;
  /** Empty state customization. */
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  className?: string;
}

/* ----------------------------------------------------------------------- */
/*                          LocalStorage helpers                           */
/* ----------------------------------------------------------------------- */

const lsRead = <T,>(key: string, fallback: T): T => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const lsWrite = (key: string, value: unknown) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* noop */
  }
};

/* ----------------------------------------------------------------------- */
/*                          Density constants (Klaret §10.5)               */
/* ----------------------------------------------------------------------- */

// Header: 40px. Body: 36px normal / 32px compact / 44px comfortable
const densityHeaderH: Record<DataTableDensity, number> = {
  compact: 40,
  normal: 40,
  comfortable: 40,
};

const densityRowH: Record<DataTableDensity, number> = {
  compact: 32,
  normal: 36,
  comfortable: 44,
};

// 12px cell padding per spec
const densityCellPadX = 'px-3'; // 12px
const densityCellPadY: Record<DataTableDensity, string> = {
  compact: 'py-1',
  normal: 'py-2',
  comfortable: 'py-3',
};

const densityFontSize: Record<DataTableDensity, string> = {
  compact: 'text-[13px]',
  normal: 'text-sm',
  comfortable: 'text-sm',
};

// Virtualize beyond ~10k rows
const VIRTUALIZE_THRESHOLD = 10_000;

/* ----------------------------------------------------------------------- */
/*                           Indeterminate checkbox                        */
/* ----------------------------------------------------------------------- */

interface IndeterminateCheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  indeterminate?: boolean;
}

const IndeterminateCheckbox = React.forwardRef<HTMLInputElement, IndeterminateCheckboxProps>(
  ({ indeterminate, className, ...props }, ref) => {
    const localRef = React.useRef<HTMLInputElement>(null);
    const resolvedRef = (ref ?? localRef) as React.RefObject<HTMLInputElement>;

    React.useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = indeterminate ?? false;
      }
    }, [indeterminate, resolvedRef]);

    return (
      <div className="relative inline-flex h-4 w-4 items-center justify-center">
        <input
          ref={resolvedRef}
          type="checkbox"
          className={cn(
            'peer absolute inset-0 h-4 w-4 cursor-pointer appearance-none rounded-[var(--tvl-r1)]',
            'border border-[var(--tvl-border-input)] bg-[var(--tvl-raised)]',
            'checked:border-[var(--tvl-accent)] checked:bg-[var(--tvl-accent)]',
            'indeterminate:border-[var(--tvl-accent)] indeterminate:bg-[var(--tvl-accent)]',
            'disabled:cursor-not-allowed disabled:opacity-40',
            className,
          )}
          {...props}
        />
        {/* Check icon — visible when checked */}
        <CheckLine
          size={10}
          className="pointer-events-none relative hidden text-[var(--tvl-on-accent)] peer-checked:block"
        />
        {/* Minus line for indeterminate — custom painted */}
        <span
          className="pointer-events-none relative hidden h-0.5 w-2.5 rounded-full bg-[var(--tvl-on-accent)] peer-indeterminate:block"
          aria-hidden
        />
      </div>
    );
  },
);
IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

/* ----------------------------------------------------------------------- */
/*                              Component                                  */
/* ----------------------------------------------------------------------- */

export function DataTable<T>({
  id,
  columns,
  data: dataProp,
  getRowId,
  loading,
  total,
  pageIndex = 0,
  pageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  sort,
  onSortChange,
  selection,
  onSelectionChange,
  bulkActions,
  filters,
  density: densityProp,
  onDensityChange,
  onRowClick,
  emptyTitle,
  emptyDescription,
  emptyAction,
  className,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const emptyTitleText = emptyTitle ?? t('table.empty.title', 'Žádná data');
  const emptyDescriptionText =
    emptyDescription ?? t('table.empty.description', 'Nejsou k dispozici žádné řádky.');
  // Defensive: a malformed `data` prop (e.g. a query hook handing back a
  // paginated `{ items }` wrapper instead of the array, or `undefined` while
  // loading) must degrade to an empty table — never crash the whole route in
  // the pagination footer where `totalRows.toLocaleString()` would throw
  // "Cannot read properties of undefined". This guards every DataTable consumer.
  const data = Array.isArray(dataProp) ? dataProp : [];

  const storageKey = id ? `teivor.tables.${id}` : null;

  /* ------------------------ Density (controlled or persisted) ----------- */
  const [densityInternal, setDensityInternal] = React.useState<DataTableDensity>(() =>
    storageKey
      ? lsRead<DataTableDensity>(`${storageKey}.density`, densityProp ?? 'normal')
      : densityProp ?? 'normal',
  );
  const density = densityProp ?? densityInternal;
  const setDensity = React.useCallback(
    (d: DataTableDensity) => {
      if (onDensityChange) onDensityChange(d);
      else setDensityInternal(d);
      if (storageKey) lsWrite(`${storageKey}.density`, d);
    },
    [onDensityChange, storageKey],
  );

  /* --------------------- Column-visibility (persisted) ------------------ */
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => {
    if (!storageKey) return {};
    const hidden = lsRead<string[]>(`${storageKey}.cols`, []);
    const vis: VisibilityState = {};
    hidden.forEach((colId) => { vis[colId] = false; });
    return vis;
  });

  const persistVisibility = (vis: VisibilityState) => {
    if (!storageKey) return;
    const hidden = Object.entries(vis)
      .filter(([, v]) => v === false)
      .map(([k]) => k);
    lsWrite(`${storageKey}.cols`, hidden);
  };

  const toggleCol = (colId: string) => {
    // pinned columns cannot be hidden
    const col = columns.find((c) => c.id === colId);
    if (col?.pinned) return;
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: prev[colId] === false ? true : false };
      persistVisibility(next);
      return next;
    });
  };

  /* ---------------------------- Column widths -------------------------- */
  const [widths, setWidths] = React.useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    columns.forEach((c) => { initial[c.id] = c.width ?? 160; });
    return initial;
  });

  const startResize = (colId: string, ev: React.PointerEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    const col = columns.find((c) => c.id === colId);
    const minW = col?.minWidth ?? 64;
    const startX = ev.clientX;
    const startW = widths[colId] ?? col?.width ?? 160;
    const onMove = (e: PointerEvent) => {
      const delta = e.clientX - startX;
      setWidths((w) => ({ ...w, [colId]: Math.max(minW, startW + delta) }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  /* --------------------- TanStack sort state bridge -------------------- */
  const tsSorting = React.useMemo<SortingState>(
    () => (sort ? [{ id: sort.id, desc: sort.dir === 'desc' }] : []),
    [sort],
  );

  const handleTsSort = React.useCallback(
    (updater: React.SetStateAction<SortingState>) => {
      if (!onSortChange) return;
      const next =
        typeof updater === 'function' ? updater(tsSorting) : updater;
      if (next.length === 0) {
        onSortChange(null);
      } else {
        onSortChange({ id: next[0].id, dir: next[0].desc ? 'desc' : 'asc' });
      }
    },
    [onSortChange, tsSorting],
  );

  /* --------------------- TanStack selection bridge --------------------- */
  const rowIdOf = React.useCallback(
    (row: T, idx: number) => (getRowId ? getRowId(row, idx) : String(idx)),
    [getRowId],
  );

  const tsRowSelection = React.useMemo<RowSelectionState>(() => {
    if (!selection) return {};
    const rs: RowSelectionState = {};
    data.forEach((row, i) => {
      const rid = rowIdOf(row, i);
      if (selection.has(rid)) rs[rid] = true;
    });
    return rs;
  }, [selection, data, rowIdOf]);

  const handleTsSelection = React.useCallback(
    (updater: React.SetStateAction<RowSelectionState>) => {
      if (!onSelectionChange) return;
      const next =
        typeof updater === 'function' ? updater(tsRowSelection) : updater;
      const ids = new Set(Object.keys(next).filter((k) => next[k]));
      // preserve selections from other pages
      const merged = new Set(selection ?? []);
      // clear current-page rows from merged
      data.forEach((row, i) => merged.delete(rowIdOf(row, i)));
      ids.forEach((id) => merged.add(id));
      onSelectionChange(merged);
    },
    [onSelectionChange, tsRowSelection, data, rowIdOf, selection],
  );

  /* --------------------- TanStack column defs -------------------------- */
  const tsCols = React.useMemo<ColumnDef<T>[]>(() => {
    const defs: ColumnDef<T>[] = [];

    // Leading selection checkbox column
    if (selection !== undefined) {
      defs.push({
        id: '__select__',
        enableSorting: false,
        enableHiding: false,
        size: 40,
        header: ({ table }) => (
          <IndeterminateCheckbox
            aria-label={t('table.select_all', 'Vybrat všechny řádky')}
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            aria-label={t('table.select_row', 'Vybrat řádek')}
            checked={row.getIsSelected()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      });
    }

    // Data columns
    columns.forEach((col) => {
      defs.push({
        id: col.id,
        enableSorting: !!(col.sortable && onSortChange),
        enableHiding: !col.pinned,
        size: widths[col.id] ?? col.width ?? 160,
        minSize: col.minWidth ?? 64,
        header: () => col.header,
        cell: ({ row }) => {
          const idx = row.index;
          const value = col.actions
            ? col.actions(row.original)
            : col.cell(row.original, idx);
          // empty cell fallback
          const isEmpty =
            value === null || value === undefined || value === '';
          return isEmpty && !col.actions ? '–' : value;
        },
        meta: { col } as Record<string, unknown>,
      });
    });

    return defs;
  }, [columns, selection, onSortChange, widths, t]);

  /* --------------------- TanStack table instance ----------------------- */
  const table = useReactTable<T>({
    data,
    columns: tsCols,
    state: {
      sorting: tsSorting,
      rowSelection: tsRowSelection,
      columnVisibility,
    },
    getRowId: (row, idx) => rowIdOf(row, idx),
    manualSorting: true,
    onSortingChange: handleTsSort,
    onRowSelectionChange: handleTsSelection,
    onColumnVisibilityChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(next);
      persistVisibility(next);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;

  /* --------------------- Virtualization -------------------------------- */
  const shouldVirtualize = data.length >= VIRTUALIZE_THRESHOLD;
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => densityRowH[density],
    overscan: 10,
    enabled: shouldVirtualize,
  });

  /* --------------------- Pagination ------------------------------------ */
  const totalRows = typeof total === 'number' && Number.isFinite(total) ? total : data.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / Math.max(1, pageSize)));
  const goTo = (p: number) => onPageChange?.(Math.max(0, Math.min(pageCount - 1, p)));

  /* --------------------- Mobile ---------------------------------------- */
  const isMobile = useMediaQuery('(max-width: 767px)');

  /* --------------------- Column-visibility menu ------------------------ */
  const [colsMenuOpen, setColsMenuOpen] = React.useState(false);
  const colsMenuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!colsMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!colsMenuRef.current?.contains(e.target as Node)) setColsMenuOpen(false);
    };
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [colsMenuOpen]);

  const selectedCount = selection?.size ?? 0;
  const cellPad = cn(densityCellPadX, densityCellPadY[density], densityFontSize[density]);

  /* ---------------------------------------------------------------------- */
  /*                              Toolbar                                   */
  /* ---------------------------------------------------------------------- */

  const Toolbar = (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-[var(--tvl-hairline)]"
      style={{ padding: '8px 12px' }}
    >
      {/* Filter chips */}
      {filters && filters.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {filters.map((f) => (
            <span
              key={f.id}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-[var(--tvl-r-full)]',
                'border border-[var(--tvl-hairline)] bg-[var(--tvl-wash-hover)]',
                'pl-2.5 pr-1.5 py-1 text-xs text-[var(--tvl-ink-2)]',
              )}
            >
              <span className="font-medium text-[var(--tvl-ink)]">{f.label}</span>
              {f.value !== undefined && <span>{f.value}</span>}
              {f.onRemove && (
                <button
                  type="button"
                  onClick={f.onRemove}
                  aria-label={`Remove filter ${typeof f.label === 'string' ? f.label : ''}`}
                  className={cn(
                    'ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full',
                    'text-[var(--tvl-ink-3)] hover:bg-[var(--tvl-hairline)] hover:text-[var(--tvl-ink)]',
                  )}
                >
                  <CloseLine size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="ml-auto flex flex-wrap items-center justify-end gap-1 sm:flex-nowrap">
        {/* Density toggle */}
        <div
          className={cn(
            'inline-flex items-center rounded-[var(--tvl-r2)]',
            'border border-[var(--tvl-hairline)] overflow-hidden',
          )}
        >
          {(
            [
              { d: 'compact', label: t('table.density.compact', 'Kompaktní'), scaleY: 0.7 },
              { d: 'normal', label: t('table.density.normal', 'Normální'), scaleY: 1 },
              { d: 'comfortable', label: t('table.density.comfortable', 'Pohodlné'), scaleY: 1.3 },
            ] as { d: DataTableDensity; label: string; scaleY: number }[]
          ).map(({ d, label, scaleY }) => (
            <button
              key={d}
              type="button"
              onClick={() => setDensity(d)}
              aria-pressed={density === d}
              title={label}
              className={cn(
                'px-2 py-1 text-xs transition-colors',
                density === d
                  ? 'bg-[var(--tvl-sunken)] text-[var(--tvl-ink)]'
                  : 'text-[var(--tvl-ink-3)] hover:text-[var(--tvl-ink)]',
              )}
            >
              <LayoutLine
                size={14}
                style={{ transform: `scaleY(${scaleY})` }}
                aria-hidden
              />
            </button>
          ))}
        </div>

        {/* Column visibility */}
        <div className="relative" ref={colsMenuRef}>
          <button
            type="button"
            onClick={() => setColsMenuOpen((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-[var(--tvl-r2)]',
              'border border-[var(--tvl-hairline)] px-2 py-1 text-xs',
              'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
            )}
            aria-haspopup="menu"
            aria-expanded={colsMenuOpen}
          >
            <TableLine size={14} aria-hidden />
            {t('table.columns', 'Sloupce')}
          </button>
          {colsMenuOpen && (
            <div
              role="menu"
              className={cn(
                'absolute right-0 z-[var(--tvl-z-popover)] mt-1 w-56 p-1',
                'rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)]',
                'bg-[var(--tvl-overlay)] shadow-[var(--tvl-e2)]',
              )}
            >
              {columns
                .filter((c) => !c.actions)
                .map((c) => {
                  const visible = columnVisibility[c.id] !== false;
                  return (
                    <label
                      key={c.id}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-[var(--tvl-r2)]',
                        'px-2 py-1.5 text-sm hover:bg-[var(--tvl-wash-hover)]',
                        c.pinned && 'cursor-not-allowed opacity-40',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={visible}
                        disabled={c.pinned}
                        onChange={() => toggleCol(c.id)}
                        className="h-3.5 w-3.5 accent-[var(--tvl-accent)]"
                      />
                      <span className="text-[var(--tvl-ink)]">{c.header}</span>
                    </label>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /* ---------------------------------------------------------------------- */
  /*                           Mobile cards                                 */
  /* ---------------------------------------------------------------------- */

  if (isMobile) {
    const headerCols = table
      .getVisibleLeafColumns()
      .filter((c) => c.id !== '__select__')
      .map((c) => columns.find((dc) => dc.id === c.id))
      .filter(Boolean) as DataTableColumn<T>[];

    // Honour the `primary` flag callers set to choose what a phone shows first.
    // This previously took the first two columns POSITIONALLY and ignored
    // `primary` altogether, so ~10 screens that had explicitly marked their
    // identifying columns (name, subject, email) silently rendered whatever
    // happened to be leftmost — often a checkbox or a status dot. Falls back to
    // the first two when no column opts in, which is what the old code did for
    // every table that has no opinion.
    const dataCols = headerCols.filter((c) => !c.actions);
    const flagged = dataCols.filter((c) => c.primary);
    const primaryCols = flagged.length > 0 ? flagged.slice(0, 2) : dataCols.slice(0, 2);
    const restCols = dataCols.filter((c) => !primaryCols.includes(c));
    const actionCol = headerCols.find((c) => !!c.actions);

    return (
      <div
        className={cn(
          'rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)]',
          className,
        )}
      >
        {Toolbar}
        {loading ? (
          <div className="space-y-3 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} shape="card" />
            ))}
          </div>
        ) : data.length === 0 ? (
          <EmptyState title={emptyTitleText} description={emptyDescriptionText} action={emptyAction} />
        ) : (
          <div className="divide-y divide-[var(--tvl-hairline)]">
            {rows.map((row) => {
              const checked = row.getIsSelected();
              return (
                <MobileCard<T>
                  key={row.id}
                  row={row.original}
                  index={row.index}
                  primaryCols={primaryCols}
                  restCols={restCols}
                  actionCol={actionCol}
                  selectable={selection !== undefined}
                  selected={checked}
                  onSelect={() => row.toggleSelected()}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                />
              );
            })}
          </div>
        )}
        {renderPagination({
          pageIndex,
          pageSize,
          pageCount,
          totalRows,
          pageSizeOptions,
          goTo,
          onPageSizeChange,
        }, t)}
        {selectedCount > 0 && bulkActions && renderBulkBar(selectedCount, bulkActions(selection!), t)}
      </div>
    );
  }

  /* ---------------------------------------------------------------------- */
  /*                          Desktop table                                 */
  /* ---------------------------------------------------------------------- */

  const headerGroups = table.getHeaderGroups();
  const visibleCols = table.getVisibleLeafColumns();

  // Virtual rows
  const virtualItems = shouldVirtualize ? rowVirtualizer.getVirtualItems() : null;
  const totalVirtualSize = shouldVirtualize ? rowVirtualizer.getTotalSize() : 0;

  const renderRow = (row: (typeof rows)[number], style?: React.CSSProperties) => {
    const checked = row.getIsSelected();
    return (
      <tr
        key={row.id}
        style={style}
        onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        className={cn(
          'group border-b border-[var(--tvl-hairline)] transition-colors',
          checked
            ? 'bg-[var(--tvl-selected)]'
            : 'hover:bg-[var(--tvl-wash-hover)]',
          onRowClick && 'cursor-pointer',
        )}
      >
        {row.getVisibleCells().map((cell) => {
          const meta = cell.column.columnDef.meta as { col?: DataTableColumn<T> } | undefined;
          const colDef = meta?.col;
          const isActions = !!colDef?.actions;
          const isSelect = cell.column.id === '__select__';

          return (
            <td
              key={cell.id}
              style={{
                width: cell.column.getSize(),
                minWidth: cell.column.columnDef.minSize,
                height: densityRowH[density],
                ...(colDef?.align === 'right'
                  ? { fontVariantNumeric: 'tabular-nums', textAlign: 'right' }
                  : colDef?.align === 'center'
                  ? { textAlign: 'center' }
                  : {}),
              }}
              className={cn(
                'align-middle text-[var(--tvl-ink)] overflow-hidden',
                cellPad,
                isSelect && 'px-2',
                isActions && 'whitespace-nowrap',
              )}
              // The row-level onClick sits on the <tr>, so a click on a row ACTION
              // bubbles into it and fires the row navigation too. The action then
              // "works" invisibly while the app navigates away — Archive archived but
              // looked like Edit, and Delete's confirm dialog was unmounted before it
              // could paint, so it looked like Edit as well. Actions and the select box
              // are the two cells that act on the row rather than opening it.
              onClick={isSelect || isActions ? (e) => e.stopPropagation() : undefined}
            >
              {isActions ? (
                // Row actions reveal on hover ONLY where the primary pointer can
                // truly hover. On touch-primary devices (`(hover: hover)` is
                // false) — phones, tablets, and 2-in-1s folded into tablet mode
                // even with a mouse attached — CSS :hover is faked/sticky, so a
                // hover-gated default-hidden control is unreachable. There we keep
                // the actions always visible. focus-within keeps them keyboard-
                // reachable everywhere.
                <div className="flex items-center gap-1 transition-opacity opacity-100 [@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              ) : (
                // Center/right columns align via flexbox, not text-align: a
                // text-align rule centers inline text (e.g. a "—" placeholder)
                // but NOT a block-level SVG icon, which would otherwise sit left
                // while text-only cells in the same column sit centered. Flex
                // centers any content identically. Left/default keeps `truncate`.
                <div
                  className={cn(
                    'min-w-0',
                    colDef?.align === 'center'
                      ? 'flex items-center justify-center'
                      : colDef?.align === 'right'
                      ? 'flex items-center justify-end'
                      : !isSelect && 'truncate',
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div
      className={cn(
        'relative rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)]',
        className,
      )}
    >
      {Toolbar}

      {/* Scrollable table container */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto"
        style={shouldVirtualize ? { height: 520, overflowY: 'auto' } : undefined}
      >
        <table
          className="w-full border-collapse"
          style={{ tableLayout: 'fixed' }}
        >
          <colgroup>
            {visibleCols.map((col) => (
              <col
                key={col.id}
                style={{ width: col.getSize() }}
              />
            ))}
          </colgroup>

          {/* Sticky header — 40px on --tvl-sunken with hairline below (§10.5) */}
          <thead
            className="sticky top-0 z-[var(--tvl-z-sticky)] bg-[var(--tvl-sunken)]"
          >
            {headerGroups.map((hg) => (
              <tr
                key={hg.id}
                style={{ height: densityHeaderH[density] }}
                className="border-b border-[var(--tvl-hairline)]"
              >
                {hg.headers.map((header) => {
                  const meta = header.column.columnDef.meta as
                    | { col?: DataTableColumn<T> }
                    | undefined;
                  const colDef = meta?.col;
                  const isSelect = header.column.id === '__select__';
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      style={{
                        width: header.getSize(),
                        textAlign:
                          colDef?.align === 'right'
                            ? 'right'
                            : colDef?.align === 'center'
                            ? 'center'
                            : 'left',
                      }}
                      className={cn(
                        'relative select-none overflow-hidden',
                        // footnote-600 --tvl-ink-2 headers (§10.5)
                        'text-[11px] font-semibold uppercase tracking-wider text-[var(--tvl-ink-2)]',
                        isSelect ? 'px-2' : cellPad,
                      )}
                    >
                      <div
                        className={cn(
                          'flex min-w-0 items-center gap-1',
                          colDef?.align === 'right' && 'justify-end',
                          colDef?.align === 'center' && 'justify-center',
                        )}
                      >
                        {isSelect ? (
                          flexRender(header.column.columnDef.header, header.getContext())
                        ) : canSort ? (
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className={cn(
                              'inline-flex min-w-0 items-center gap-1',
                              'hover:text-[var(--tvl-ink)]',
                            )}
                            aria-sort={
                              sorted === 'asc'
                                ? 'ascending'
                                : sorted === 'desc'
                                ? 'descending'
                                : 'none'
                            }
                          >
                            {/* truncate so a long header can't spill into the next column */}
                            <span className="min-w-0 truncate">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            {/* Sort asc→desc→off icons (§10.5) */}
                            <span className="inline-flex shrink-0 flex-col -space-y-1.5 opacity-40 aria-[sort=ascending]:opacity-100 aria-[sort=descending]:opacity-100">
                              <UpSmallLine
                                size={16}
                                className={cn(sorted === 'asc' ? 'opacity-100' : 'opacity-40')}
                              />
                              <DownSmallLine
                                size={16}
                                className={cn(sorted === 'desc' ? 'opacity-100' : 'opacity-40')}
                              />
                            </span>
                          </button>
                        ) : (
                          <span className="min-w-0 truncate">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        )}
                      </div>

                      {/* Column resize handle */}
                      {!isSelect && (
                        <span
                          onPointerDown={(e) => startResize(header.column.id, e)}
                          className={cn(
                            'absolute right-0 top-0 h-full w-1 cursor-col-resize',
                            'hover:bg-[var(--tvl-accent)]/40',
                          )}
                          aria-hidden
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading ? (
              // 8 skeleton rows (§10.5)
              Array.from({ length: 8 }).map((_, i) => (
                <tr
                  key={i}
                  style={{ height: densityRowH[density] }}
                  className="border-b border-[var(--tvl-hairline)]"
                >
                  {visibleCols.map((col) => (
                    <td
                      key={col.id}
                      className={cn(cellPad, 'align-middle')}
                    >
                      <Skeleton lines={1} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleCols.length}
                  className="p-0"
                >
                  <EmptyState
                    title={emptyTitleText}
                    description={emptyDescriptionText}
                    action={emptyAction}
                  />
                </td>
              </tr>
            ) : shouldVirtualize && virtualItems ? (
              <>
                {/* top spacer */}
                {virtualItems[0]?.start > 0 && (
                  <tr>
                    <td
                      colSpan={visibleCols.length}
                      style={{ height: virtualItems[0].start }}
                    />
                  </tr>
                )}
                {virtualItems.map((vItem) => {
                  const row = rows[vItem.index];
                  return renderRow(row, { height: vItem.size });
                })}
                {/* bottom spacer */}
                {(() => {
                  const last = virtualItems[virtualItems.length - 1];
                  const bottomSpace = last
                    ? totalVirtualSize - last.start - last.size
                    : 0;
                  return bottomSpace > 0 ? (
                    <tr>
                      <td
                        colSpan={visibleCols.length}
                        style={{ height: bottomSpace }}
                      />
                    </tr>
                  ) : null;
                })()}
              </>
            ) : (
              rows.map((row) => renderRow(row))
            )}
          </tbody>
        </table>
      </div>

      {renderPagination({
        pageIndex,
        pageSize,
        pageCount,
        totalRows,
        pageSizeOptions,
        goTo,
        onPageSizeChange,
      }, t)}
      {selectedCount > 0 && bulkActions && renderBulkBar(selectedCount, bulkActions(selection!), t)}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*                          Mobile card                                    */
/* ----------------------------------------------------------------------- */

interface MobileCardProps<T> {
  row: T;
  index: number;
  primaryCols: DataTableColumn<T>[];
  restCols: DataTableColumn<T>[];
  actionCol?: DataTableColumn<T>;
  selectable: boolean;
  selected: boolean;
  onSelect: () => void;
  onClick?: () => void;
}

function MobileCard<T>({
  row,
  index,
  primaryCols,
  restCols,
  actionCol,
  selectable,
  selected,
  onSelect,
  onClick,
}: MobileCardProps<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [primary1, primary2] = primaryCols;

  return (
    // §10.5: rows become stacked --tvl-r3 record cards below 768px
    <div
      className={cn(
        'rounded-[var(--tvl-r3)] p-3 transition-colors',
        selected ? 'bg-[var(--tvl-selected)]' : 'hover:bg-[var(--tvl-wash-hover)]',
      )}
    >
      <div className="flex flex-wrap items-start gap-3">
        {selectable && (
          <input
            aria-label={t('table.select_row', 'Vybrat řádek')}
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            onClick={(e) => e.stopPropagation()}
            className="mt-1 h-4 w-4 accent-[var(--tvl-accent)]"
          />
        )}
        <button
          type="button"
          onClick={onClick ?? (() => setOpen((v) => !v))}
          className="min-w-0 flex-1 text-left"
        >
          {/* Two lines, not one truncated line. Measured with real content, a
              single `truncate` row lost up to 326px of a Czech company name or
              summary — you saw roughly the first fifth and an ellipsis. A card
              has vertical room; spending two lines on the row's identity is the
              whole point of the card layout.
              `[&_*]:min-w-0` + `break-words` reach the caller's own <span>: a
              cell renderer returns arbitrary markup, and a long unbroken string
              (a 50-character email, a domain) inside it would otherwise push
              past the card no matter what this wrapper does. */}
          {primary1 && (
            <div className="line-clamp-2 break-words text-sm font-medium text-[var(--tvl-ink)] [&_*]:min-w-0 [&_*]:break-words">
              {primary1.cell(row, index) ?? '–'}
            </div>
          )}
          {primary2 && (
            <div className="mt-0.5 line-clamp-2 break-words text-xs text-[var(--tvl-ink-2)] [&_*]:min-w-0 [&_*]:break-words">
              {primary2.cell(row, index) ?? '–'}
            </div>
          )}
        </button>
        {actionCol && (
          <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-1 sm:w-auto">
            {actionCol.actions?.(row)}
          </div>
        )}
      </div>
      {restCols.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className={cn(
              'mt-1 flex w-full items-center gap-1 rounded-[var(--tvl-r2)] py-2',
              'text-xs text-[var(--tvl-accent)]',
              'active:bg-[var(--tvl-wash-pressed)]',
            )}
          >
            <span>
              {open
                ? t('table.hide_details', 'Skrýt detaily')
                : t('table.show_details', 'Zobrazit detaily')}
            </span>
            {/* A caret makes the row read as expandable at a glance; the bare
                text link gave no hint that anything was hidden. */}
            <DownSmallLine
              size={16}
              aria-hidden
              className={cn('transition-transform', open && 'rotate-180')}
            />
          </button>
          {open && (
            // `auto` + `minmax(0,1fr)`: a plain `1fr` refuses to shrink below its
            // content, so one long unbroken value (a URL, a message id) pushed the
            // grid wider than the phone and clipped every value in the column.
            <dl className="mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
              {restCols.map((c) => (
                <React.Fragment key={c.id}>
                  <dt className="text-[var(--tvl-ink-3)]">{c.header}</dt>
                  {/* Same reach-the-caller's-markup problem as the primary
                      cells: the <dd> can shrink, but a long unbroken value in
                      the cell renderer's own <span> could not, and overflowed
                      the card by up to 305px. */}
                  <dd className="min-w-0 break-words text-[var(--tvl-ink)] [&_*]:min-w-0 [&_*]:break-words">
                    {c.cell(row, index) ?? '–'}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*                          Pagination footer (§10.5)                      */
/* ----------------------------------------------------------------------- */

function renderPagination(args: {
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  totalRows: number;
  pageSizeOptions: number[];
  goTo: (p: number) => void;
  onPageSizeChange?: (n: number) => void;
}, t: ReturnType<typeof useTranslation>['t']) {
  const { pageIndex, pageSize, pageCount, totalRows, pageSizeOptions, goTo, onPageSizeChange } =
    args;
  const start = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const end = Math.min(totalRows, (pageIndex + 1) * pageSize);

  // Page window: up to 5 pages centred on current
  const windowStart = Math.max(0, Math.min(pageIndex - 2, pageCount - 5));
  const windowEnd = Math.min(pageCount, windowStart + 5);
  const pages: number[] = [];
  for (let i = windowStart; i < windowEnd; i++) pages.push(i);

  return (
    // 48px footer, hairline above (§10.5)
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-[var(--tvl-hairline)] px-3 py-2 sm:flex-nowrap sm:py-0',
        'text-xs text-[var(--tvl-ink-3)]',
      )}
      // min-height, not height: the row is `flex-wrap`, and at phone widths the
      // page-size select + range + page buttons genuinely need a second line.
      // A fixed 48px box CLIPPED that second line, so the pager buttons became
      // invisible and unreachable on exactly the screens that wrap.
      style={{ minHeight: 48 }}
    >
      <div className="flex items-center gap-1.5">
        <span>{t('table.rows_per_page', 'Řádků na stránku')}</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          className={cn(
            'rounded-[var(--tvl-r2)] border border-[var(--tvl-hairline)]',
            'bg-[var(--tvl-raised)] px-1.5 py-1 text-xs text-[var(--tvl-ink)]',
          )}
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      {/* "1–50 of 1,240" format (§10.5) */}
      <div>
        {start.toLocaleString()}–{end.toLocaleString()} of {totalRows.toLocaleString()}
      </div>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-1 sm:flex-nowrap">
        <PgBtn
          onClick={() => goTo(0)}
          disabled={pageIndex === 0}
          aria-label={t('table.page.first', 'První stránka')}
        >
          {'«'}
        </PgBtn>
        <PgBtn
          onClick={() => goTo(pageIndex - 1)}
          disabled={pageIndex === 0}
          aria-label={t('table.page.previous', 'Předchozí stránka')}
        >
          {'‹'}
        </PgBtn>
        {pages.map((p) => (
          <PgBtn
            key={p}
            onClick={() => goTo(p)}
            active={p === pageIndex}
            aria-label={t('table.page.n', { n: p + 1, defaultValue: 'Stránka {{n}}' })}
          >
            {p + 1}
          </PgBtn>
        ))}
        <PgBtn
          onClick={() => goTo(pageIndex + 1)}
          disabled={pageIndex >= pageCount - 1}
          aria-label={t('table.page.next', 'Další stránka')}
        >
          {'›'}
        </PgBtn>
        <PgBtn
          onClick={() => goTo(pageCount - 1)}
          disabled={pageIndex >= pageCount - 1}
          aria-label={t('table.page.last', 'Poslední stránka')}
        >
          {'»'}
        </PgBtn>
      </div>
    </div>
  );
}

const PgBtn: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }
> = ({ active, className, ...props }) => (
  <button
    type="button"
    className={cn(
      'inline-flex h-7 min-w-[28px] items-center justify-center rounded-[var(--tvl-r2)] px-1.5 text-xs',
      active
        ? 'bg-[var(--tvl-accent)] text-[var(--tvl-on-accent)]'
        : 'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] hover:text-[var(--tvl-ink)]',
      'disabled:pointer-events-none disabled:opacity-40',
      className,
    )}
    {...props}
  />
);

/* ----------------------------------------------------------------------- */
/*                           Bulk action bar                               */
/* ----------------------------------------------------------------------- */

function renderBulkBar(count: number, actions: React.ReactNode, t: ReturnType<typeof useTranslation>['t']) {
  return (
    <div
      role="region"
      aria-label={t('table.bulk_actions', 'Hromadné akce')}
      className={cn(
        'sticky bottom-0 z-[var(--tvl-z-sticky)] flex flex-wrap items-center gap-3',
        'border-t border-[var(--tvl-hairline)] bg-[var(--tvl-sunken)] px-3 py-2',
        'shadow-[var(--tvl-e2)]',
      )}
    >
      <span className="text-sm font-medium text-[var(--tvl-ink)]">
        {t('table.selected', { count, defaultValue: '{{count}} vybráno' })}
      </span>
      <div className="ml-auto flex items-center gap-2">{actions}</div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*                    Row actions menu trigger helper                       */
/* ----------------------------------------------------------------------- */

/**
 * Convenience wrapper consumed by callers building the trailing ⋯ (More1Line)
 * row-actions column. Pass as `col.actions` return value.
 * Not exported — callers compose their own menus; this is internal sugar.
 */
function RowActionsIcon() {
  return (
    <More1Line
      size={20}
      className="text-[var(--tvl-ink-3)] hover:text-[var(--tvl-ink)]"
      aria-hidden
    />
  );
}
// keep it tree-shakeable; expose only if needed externally
export { RowActionsIcon };
